// Reunderwriting Module (Tier 3) PR 3 — analyst tab shell. Used by the
// case-detail page for the Underwriting / Financial Analysis / Legal
// Analysis / Business Analysis tabs.
//
// Mirrors the Deal Flow AnalystTabContent template (criteria checklist
// + lane pass/fail), adapted to a ReunderwritingCase. The Underwriting
// lane (A1) also renders the combined-outcome panel on the right rail
// because A1 is the case owner.

import { Card } from "@/design-system/components/ui";
import { ReunderwritingCriteriaChecklist } from "./ReunderwritingCriteriaChecklist";
import { ReunderwritingLanePassFailSection } from "./ReunderwritingLanePassFailSection";
import { ReunderwritingOutcomePanel } from "./ReunderwritingOutcomePanel";
import { TabPlaceholderCard } from "@/components/DealFlow/common/TabPlaceholderCard";
import type { CriteriaLane, ReunderwritingCaseDto } from "@/types/reunderwriting";

const LANE_LABEL: Record<CriteriaLane, string> = {
  A1: "A1 Underwriting",
  A2: "Financial Analysis",
  A3: "Legal Analysis",
  A4: "Business Analysis",
};

interface Props {
  rcase: ReunderwritingCaseDto;
  lane: CriteriaLane;
  readOnly?: boolean;
}

export function ReunderwritingAnalystTab({ rcase, lane, readOnly = false }: Props) {
  // A case is born into Underwriting so the gate below is effectively
  // always passed; we keep it so a Declined/Withdrawn case still shows
  // a sane "not applicable" message instead of an empty form.
  const stageOk =
    rcase.stage === "Underwriting" || rcase.stage === "Closing" || rcase.stage === "Closed";
  const laneLabel = LANE_LABEL[lane];

  if (!stageOk) {
    return (
      <TabPlaceholderCard
        title={`${laneLabel} — not active in this stage`}
        body={`The case is currently ${rcase.stage}. Analyst work is only available in Underwriting → Closing.`}
      />
    );
  }

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-3 gap-space-4"
      data-testid={`reunderwriting-analyst-tab-${lane}`}
    >
      <div className="lg:col-span-2 flex flex-col gap-space-3">
        <Card>
          <h3 className="font-display text-h3 m-0 mb-space-1">
            {laneLabel} — Criteria Checklist
          </h3>
          <p className="text-caption text-gray-mid m-0 mb-space-3">
            Certify, Decline, Escalate, or (E2-only) Waive each item. Certify
            and Decline are gated on a Severity Assessment + Report Attestation.
          </p>
          <ReunderwritingCriteriaChecklist caseId={rcase.id} lane={lane} readOnly={readOnly} />
        </Card>

        <ReunderwritingLanePassFailSection caseId={rcase.id} lane={lane} readOnly={readOnly} />
      </div>

      <div className="flex flex-col gap-space-3">
        <ReunderwritingOutcomePanel caseId={rcase.id} canFinalize={lane === "A1" && !readOnly} />
      </div>
    </div>
  );
}
